import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { adminAPI, handleApiError } from '../../../services/api';
import './SystemAnnouncementComposer.css';

const SystemAnnouncementComposer = () => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info', // info, success, warning, error
    active_days: '' // optional; blank = platform default
  });

  const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
  const [isLoadingCurrent, setIsLoadingCurrent] = useState(true);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null); // { type: 'success'|'error', text: '' }

  useEffect(() => {
    const loadCurrent = async () => {
      try {
        const response = await adminAPI.getCurrentAnnouncement();
        const announcement = response?.data?.data;
        if (announcement) {
          setCurrentAnnouncement(announcement);
          setFormData({
            title: announcement.title || '',
            message: announcement.message || '',
            type: announcement.type || 'info',
            active_days: announcement.active_days != null ? String(announcement.active_days) : ''
          });
        }
      } catch (error) {
        // Non-fatal — composer still works for creating new announcements
        console.error('Failed to load current announcement:', error);
      } finally {
        setIsLoadingCurrent(false);
      }
    };
    loadCurrent();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePreviewSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.message.trim()) {
      setSubmitMessage({ type: 'error', text: t('announcements.requiredFields', 'Title and message are required.') });
      return;
    }
    setShowConfirmModal(true);
  };

  const confirmSendAnnouncement = async () => {
    setIsSubmitting(true);
    setSubmitMessage(null);
    setShowConfirmModal(false);
    
    try {
      const payload = {
        ...formData,
        active_days: formData.active_days === '' ? null : Number(formData.active_days)
      };
      const response = await adminAPI.sendAnnouncement(payload);
      setCurrentAnnouncement(response?.data?.data || payload);
      setSubmitMessage({ type: 'success', text: t('announcements.success', 'Announcement sent successfully.') });
      // clear success message after 5 seconds
      setTimeout(() => setSubmitMessage(null), 5000);
    } catch (error) {
      setSubmitMessage({ type: 'error', text: handleApiError(error) });
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmClearAnnouncement = async () => {
    setIsClearing(true);
    setSubmitMessage(null);
    setShowClearModal(false);

    try {
      await adminAPI.clearAnnouncement();
      setCurrentAnnouncement(null);
      setSubmitMessage({ type: 'success', text: t('announcements.clearSuccess', 'Current announcement cleared. No one will see it anymore.') });
      setTimeout(() => setSubmitMessage(null), 5000);
    } catch (error) {
      setSubmitMessage({ type: 'error', text: handleApiError(error) });
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <div className="system-announcement-composer">
      <div className="composer-header">
        <h3>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
          </svg>
          {t('announcements.title', 'Send System Announcement')}
        </h3>
        <p>{t('announcements.description', 'Broadcast a message to all users across the platform.')}</p>
      </div>

      {submitMessage && (
        <div className={`composer-alert alert-${submitMessage.type}`}>
          {submitMessage.text}
        </div>
      )}

      {currentAnnouncement && (
        <div className="composer-alert alert-info">
          {currentAnnouncement.expires_at
            ? t('announcements.currentActiveWithExpiry', 'An announcement is currently active. It stops being delivered after {{date}}.', {
                date: new Date(currentAnnouncement.expires_at).toLocaleDateString()
              })
            : t('announcements.currentActive', 'An announcement is currently active. Editing and sending will replace it.')}
        </div>
      )}

      <form className="composer-form" onSubmit={handlePreviewSubmit}>
        <div className="form-group">
          <label htmlFor="title">{t('announcements.formTitle', 'Announcement Title')} <span className="required">*</span></label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder={t('announcements.titlePlaceholder', 'e.g., Scheduled Maintenance')}
            disabled={isSubmitting}
            required
            maxLength={100}
          />
        </div>

        <div className="form-row">
        <div className="form-group">
          <label htmlFor="type">{t('announcements.formType', 'Announcement Type')}</label>
          <select
            id="type"
            name="type"
            value={formData.type}
            onChange={handleChange}
            disabled={isSubmitting}
          >
            <option value="info">{t('announcements.typeInfo', 'Information')}</option>
            <option value="success">{t('announcements.typeSuccess', 'Success')}</option>
            <option value="warning">{t('announcements.typeWarning', 'Warning / Alert')}</option>
            <option value="error">{t('announcements.typeError', 'Error / Critical')}</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="active_days">{t('announcements.activeDays', 'Active Period (days)')}</label>
          <input
            type="number"
            id="active_days"
            name="active_days"
            value={formData.active_days}
            onChange={handleChange}
            placeholder={t('announcements.activeDaysPlaceholder', 'Default (7 days)')}
            disabled={isSubmitting}
            min={0}
            max={365}
          />
          <small style={{ color: '#64748b', fontSize: '12px', marginTop: '4px', display: 'block' }}>
            {t('announcements.activeDaysHint', 'Each user sees the announcement once. After this many days it stops being delivered to anyone. Leave empty to use the default.')}
          </small>
        </div>
        </div>

        <div className="form-group">
          <label htmlFor="message">{t('announcements.formMessage', 'Message Content')} <span className="required">*</span></label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder={t('announcements.messagePlaceholder', 'Write the announcement details here...')}
            disabled={isSubmitting}
            required
            rows={6}
            maxLength={1000}
          />
          <div className="char-counter">
            {formData.message.length} / 1000
          </div>
        </div>

        <div className="form-actions">
          {currentAnnouncement && (
          <button
            type="button"
            className="btn-clear-announcement"
            onClick={() => setShowClearModal(true)}
            disabled={isSubmitting || isClearing}
            style={{
              marginRight: '12px',
              padding: '10px 18px',
              borderRadius: '8px',
              border: '1px solid #dc2626',
              background: 'transparent',
              color: '#dc2626',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {isClearing ? (
              <span className="loading-spinner-small"></span>
            ) : (
              t('announcements.clearBtn', 'Clear Current Announcement')
            )}
          </button>
          )}
          <button 
            type="submit" 
            className="btn-submit"
            disabled={isSubmitting || isLoadingCurrent}
          >
            {isSubmitting ? (
              <span className="loading-spinner-small"></span>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
                {t('announcements.sendBtn', 'Send Announcement')}
              </>
            )}
          </button>
        </div>
      </form>

      {showConfirmModal && (
        <div className="announcement-modal-overlay">
          <div className="announcement-modal">
            <div className="modal-header">
              <h4>{t('announcements.confirmTitle', 'Confirm Announcement')}</h4>
            </div>
            <div className="modal-body">
              <p>{t('announcements.confirmText', 'Are you sure you want to broadcast this announcement to all users? This action cannot be undone.')}</p>
              
              <div className={`announcement-preview type-${formData.type}`}>
                <h5>{formData.title}</h5>
                <span className={`preview-badge badge-${formData.type}`}>{formData.type.toUpperCase()}</span>
                <p>{formData.message}</p>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-cancel" 
                onClick={() => setShowConfirmModal(false)}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button 
                className="btn-confirm" 
                onClick={confirmSendAnnouncement}
              >
                {t('announcements.confirmSend', 'Yes, Broadcast Now')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showClearModal && (
        <div className="announcement-modal-overlay">
          <div className="announcement-modal">
            <div className="modal-header">
              <h4>{t('announcements.clearConfirmTitle', 'Clear Announcement')}</h4>
            </div>
            <div className="modal-body">
              <p>{t('announcements.clearConfirmText', 'This removes the current system announcement immediately. Users who have not seen it yet will never receive it. This cannot be undone.')}</p>
            </div>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setShowClearModal(false)}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                className="btn-confirm"
                style={{ background: '#dc2626' }}
                onClick={confirmClearAnnouncement}
              >
                {t('announcements.clearConfirm', 'Yes, Clear It')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemAnnouncementComposer;
